import { CncReportLocal, Project } from '@gameonly/core';
import { Alert, DialogActions, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import useProjects from '../project/context/useProjects';
import Button, { WhiteButton } from '../ui/Button';
import useReport from './context/useReport';
import Loader from '../ui/Loader';

interface CreateLocalInternalProps {
  onClose: () => void;
  onSave: (local: Partial<CncReportLocal>) => void;
}

interface InternalValues {
  projectId: string;
  inputValue: string | number;
}

const initialValues: InternalValues = {
  inputValue: '',
  projectId: '',
};

const getValidationSchema = (t: (key: string) => string) => {
  return Yup.object().shape({
    projectId: Yup.string().required(
      t('create-modal-internal.projectId-required'),
    ),
    inputValue: Yup.number()
      .typeError(t('create-modal-internal.value-must-be-number'))
      .positive(t('create-modal-internal.value-positive')),
  });
};

const CreateLocalInternal = ({ onSave, onClose }: CreateLocalInternalProps) => {
  const { t } = useTranslation(['cnc-reports', 'common', 'project']);
  const { projects, loading } = useProjects({
    projectType: 'site',
    validated: true,
    isProratized: false,
  });
  const { reportId } = useParams();
  const { report } = useReport(reportId);

  const availableProjects = projects?.filter(
    (project) =>
      !report?.locals.find((local) => local.id === project._id),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (loading) {
    return <Loader fullPage />;
  }

  if (!availableProjects || availableProjects.length === 0) {
    return <p className="hxr">{t('create-modal-internal.no-projects')}</p>;
  }

  const handleValidate = (values: InternalValues) => {
    const project = availableProjects.find(
      (project) => project._id.toString() === values.projectId,
    );
    if (!project) {
      setErrorMessage(t('create-modal-internal.project-not-found'));
      return;
    }

    const local = getInternalLocal(values, project);

    if (!local) {
      setErrorMessage(t('create-modal-internal.local-creation-error'));
      return;
    }
    onSave(local);
    onClose();
  };

  return (
    <>
      <Formik<InternalValues>
        initialValues={initialValues}
        validationSchema={getValidationSchema(t)}
        onSubmit={handleValidate}
      >
        {({ values, setFieldValue, errors, touched, handleChange }) => (
          <Form>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage(null)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            <Autocomplete
              options={availableProjects}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => {
                if (value) {
                  setFieldValue('projectId', value._id);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('create-modal-internal.select-project')}
                  margin="normal"
                  fullWidth
                  error={touched.projectId && Boolean(errors.projectId)}
                  helperText={touched.projectId && errors.projectId}
                />
              )}
              value={
                availableProjects.find(
                  (project) => project._id.toString() === values.projectId,
                ) || null
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
            />

            <DialogActions>
              <WhiteButton onClick={onClose}>{t('common:cancel')}</WhiteButton>
              <Button type="submit" color="primary">
                {t('common:validate')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </>
  );
};

const getInternalLocal = (values: InternalValues, project: Project) => {
  const local: Partial<CncReportLocal> = {
    id: project._id,
    type: 'internal',
    validated: true,
    name: project.name,
  };
  return local;
};

export default CreateLocalInternal;
